import { useLatest } from 'ahooks';
import { useEffect, useRef } from 'react';
import { isHotKeyPress, type ITouchKitEvent } from '@/utils/event-kit';
import type { IPoint } from '@/version-types';
import { MoveListenerLevel } from '../../shared';
import { getFramework } from '../framework';
import { InstanceData, InstanceDataType } from '../instance-data';
import { useIdentityState } from '../states/identity-state';

export const useIdentity = () => {
    return useIdentityState()((state) => state.id);
};

export const useFramework = () => {
    const identity = useIdentity();
    return getFramework(identity);
};

export function useMountEffect(callback: () => void | (() => void)) {
    useEffect(callback, []);
}

/** 移动事件监听 */
export const useMoveEventListener = (
    handler: (event: ITouchKitEvent) => void,
    /** level越大, 越先执行 */
    level = MoveListenerLevel.DEFAULT_LEVEL,
    name = '',
) => {
    const identity = useIdentity();
    // console.log('useMoveEventListener', identity);
    const startMoveEvent = useRef<ITouchKitEvent | null>(null);
    const realMoveCallbackCount = useRef(0);

    const listener = (event: ITouchKitEvent) => {
        if (event.isStart) {
            startMoveEvent.current = event;
            return;
        }
        if (event.isEnd) {
            if (realMoveCallbackCount.current > 0) {
                handler(event);
            }
            // reset
            startMoveEvent.current = null;
            realMoveCallbackCount.current = 0;
            return;
        }
        /**
         * 在Mac触控板上，即使手指没有移动，也有可能触发mousemove事件
         * 这可能是由于触控板的精确度和敏感度造成的。当手指接触触控板时，即使微微晃动或触摸压力发生微小变化，也会被视为移动。
         * 所以需要过滤掉
         */
        if (startMoveEvent.current && startMoveEvent.current.x === event.x && startMoveEvent.current.y === event.y)
            return;

        if (realMoveCallbackCount.current === 0 && startMoveEvent.current) {
            handler(startMoveEvent.current);
        }

        handler(event);
        realMoveCallbackCount.current++;
    };

    const listenerRef = useLatest(listener);

    useEffect(() => {
        const tkListenerList = InstanceData.getData(identity, InstanceDataType.TouchListenerList) as {
            name: string;
            level: MoveListenerLevel;
            listenerRef: React.RefObject<(event: ITouchKitEvent) => void>;
        }[];
        const listenerHandle = `${name}-${identity}`;
        // 加入事件队列
        tkListenerList.push({
            name: listenerHandle,
            level,
            listenerRef,
        });

        // 移除事件队列
        return () => {
            const index = tkListenerList.findIndex((item) => item.listenerRef === listenerRef);
            if (index !== -1) {
                tkListenerList.splice(index, 1);
            }
        };
    }, []);
};

/** 移动事件工具类 */
export class PointMove {
    private startPoint: IPoint | null = null;
    private endPoint: IPoint | null = null;

    setStart(p: IPoint) {
        this.startPoint = p;
    }

    setEnd(p: IPoint) {
        this.endPoint = p;
    }

    isMove() {
        return (
            this.startPoint &&
            this.endPoint &&
            (this.startPoint.x !== this.endPoint.x || this.startPoint.y !== this.endPoint.y)
        );
    }
}

interface IClickEvents {
    delay?: number;
    onClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    onDoubleClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    onMouseDown?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    onMouseUp?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
}
/**
 * 使用元素点击事件
 * @param layerId - 图层ID
 * @param onClick - 点击事件
 * @param onDoubleClick - 双击事件
 * @returns 点击事件
 */
export const useElementClickEvents = (
    layerId: string,
    { onClick, onDoubleClick, onMouseDown, onMouseUp, delay = 300 }: IClickEvents,
) => {
    const clickTimer = useRef<NodeJS.Timeout | null>(null);
    const activeTimerRef = useRef<number>(new Date().getTime());
    const isMove = useRef(false);
    const downPoint = useRef<IPoint>({ x: 0, y: 0 });

    useEffect(() => {
        activeTimerRef.current = new Date().getTime();
    }, [layerId]);

    function processClick() {
        if (isHotKeyPress()) return false;
        // 选中组内元素，需要延迟处理点击，防止从父级继承过来的点击
        // ATTENTION: 将时间判断减少到 100 ms，修复非组内的文本图层快速双击无法直接进入编辑态的问题
        if (new Date().getTime() - activeTimerRef.current < 100) return false;
        if (isMove.current) return false;
        if (clickTimer.current) {
            clearTimeout(clickTimer.current);
            clickTimer.current = null;
        }
        return true;
    }

    return {
        onMouseDown: (e) => {
            downPoint.current = { x: e.pageX || 0, y: e.pageY || 0 };
            isMove.current = false;
            onMouseDown?.(e);
        },
        onMouseUp: (e) => {
            const currentPoint = { x: e.pageX || 0, y: e.pageY || 0 };
            if (currentPoint.x !== downPoint.current.x || currentPoint.y !== downPoint.current.y) {
                isMove.current = true;
            }
            onMouseUp?.(e);
        },
        onClick: (e) => {
            e.stopPropagation();
            if (!processClick()) return;
            clickTimer.current = setTimeout(() => {
                onClick?.(e);
            }, delay);
        },
        onDoubleClick: (e) => {
            e.stopPropagation();
            if (!processClick()) return;
            onDoubleClick?.(e);
        },
    };
};
